import { useQuery, useMutation, useTransport, createConnectQueryKey, createQueryOptions } from '@connectrpc/connect-query'
import { keepPreviousData, useQueries, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { create } from '@bufbuild/protobuf'
import { useStore } from '@tanstack/react-store'
import { useMemo, useRef } from 'react'
import type { Transport } from '@connectrpc/connect'

import { listQuotes, getQuote, getQuoteDetail, createQuote, deleteQuote, updateQuote, updateQuoteItem, sendQuote, duplicateQuote, generateQuotePdf, getQuoteLitesByIds, getInquiry, listInquiries, listContactQuoteInteractions, getQuoteStats, getDashboardSummary } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListQuotesRequestSchema, GetQuoteRequestSchema, GetQuoteResponseSchema, GetQuoteDetailRequestSchema, GetQuoteLitesByIdsRequestSchema, GetQuoteLitesByIdsResponseSchema, GetInquiryResponseSchema, ListContactQuoteInteractionsRequestSchema, GetQuoteStatsRequestSchema, SendQuoteRequestSchema, DeleteQuoteRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import type { ListQuotesResponse } from '@/gen/cortado/v1/cortado_pb'
import { QuoteLiteSchema, QuoteLineItemSchema, type ContactQuoteInteraction, type Quote } from '@/gen/cortado/v1/invoices_pb'
import { type QuoteStatusEnum, type ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { type QuoteLineItem } from '@/gen/cortado/v1/invoices_pb'
import { organizationStore, useCurrentOrganization } from './organization.store'
import { dateToTimestamp } from '@/lib/date-utils'
import { connectTransport } from '@/lib/connect-transport'
import { calculateLineTotalCents } from '@/lib/line-item-calculations'
import { invalidateCrossEntityEmailCaches } from '@/stores/email-thread-cache'

export interface ListQuotesQueryOptions {
  organizationId: string
  inquiryId?: string
  statuses?: QuoteStatusEnum[]
  since?: Date
  limit?: number
  offset?: number
  search?: string
}

export function createListQuotesRequest(options: ListQuotesQueryOptions) {
  return create(ListQuotesRequestSchema, {
    organizationId: options.organizationId,
    inquiryId: options.inquiryId,
    statuses: options.statuses || [],
    since: options.since ? dateToTimestamp(options.since) : undefined,
    limit: options.limit,
    offset: options.offset,
    search: options.search || undefined,
  })
}

export function listQuotesQueryOptions(
  options: ListQuotesQueryOptions,
  transport: Transport = connectTransport,
) {
  return createQueryOptions(listQuotes, createListQuotesRequest(options), { transport })
}

export function getQuoteQueryOptions(quoteId: string, transport: Transport = connectTransport) {
  return createQueryOptions(getQuote, create(GetQuoteRequestSchema, { id: quoteId }), { transport })
}

/**
 * Query options for the GetQuoteDetail aggregate RPC, which returns the quote
 * plus its originating inquiry, contact, parent booking, and (when converted)
 * the created invoice in a single round-trip. Used by the quote detail route
 * loader to warm the per-entity caches and collapse the
 * quote -> inquiry -> booking -> invoice request waterfall.
 */
export function getQuoteDetailQueryOptions(quoteId: string, transport: Transport = connectTransport) {
  return createQueryOptions(getQuoteDetail, create(GetQuoteDetailRequestSchema, { id: quoteId }), { transport })
}

/**
 * Converts a full Quote to a QuoteLite for cache updates
 * This allows us to update the getQuoteLitesByIds cache directly
 * without requiring a refetch when a new quote is created.
 */
function quoteToQuoteLite(quote: Quote) {
  return create(QuoteLiteSchema, {
    id: quote.id,
    organizationId: quote.organizationId,
    inquiryId: quote.inquiryId,
    contactId: quote.contactId,
    quoteNumber: quote.quoteNumber,
    title: quote.title,
    totalCents: quote.totalCents,
    status: quote.status,
    sentAt: quote.sentAt,
    validUntil: quote.validUntil,
    createdAt: quote.createdAt,
    requiresQuestionnaire: quote.requiresQuestionnaire,
    // Note: contact_name is not available from Quote, backend populates it
  })
}

/**
 * Compares two arrays of line items to detect changes
 * Returns true if items differ in count, order, content, or properties
 *
 * This helper is useful for determining if line items have changed before
 * updating a quote, as line item changes trigger new revisions in the backend.
 *
 * @param original - The original line items from the quote
 * @param updated - The updated line items to compare
 * @returns true if line items have changed, false otherwise
 */
export function hasLineItemsChanged(
  original: QuoteLineItem[] | undefined,
  updated: QuoteLineItem[]
): boolean {
  if (!original) return updated.length > 0
  if (original.length !== updated.length) return true

  // Compare each item's relevant properties
  return original.some((origItem, index) => {
    const updatedItem = updated[index]
    if (!updatedItem) return true

    return (
      origItem.sourceItemId !== updatedItem.sourceItemId ||
      origItem.quantity !== updatedItem.quantity ||
      origItem.unitPriceCents !== updatedItem.unitPriceCents ||
      origItem.description !== updatedItem.description ||
      origItem.unit !== updatedItem.unit ||
      origItem.sortOrder !== updatedItem.sortOrder
    )
  })
}

/**
 * Hook to fetch quotes for an inquiry or organization
 * 
 * This hook:
 * - Fetches quotes for the selected organization
 * - Uses lightweight QuoteLite format optimized for list views
 * - Supports filtering by inquiry ID, statuses, dates, and pagination
 * - Returns lightweight quote data with pre-calculated totals
 * 
 * @param options - Optional filters for inquiry ID, statuses, dates, and pagination
 * @returns Query result with quotes array (QuoteLite[]), total count, loading state, and error
 */
export function useQuotesList(options?: {
  inquiryId?: string
  statuses?: QuoteStatusEnum[]
  since?: Date
  limit?: number
  offset?: number
  search?: string
  // Keep the previous result while a new search loads, so the page can keep
  // its table mounted instead of falling back to a full loading state.
  keepPreviousData?: boolean
}) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  const request = useMemo(() => {
    return createListQuotesRequest({
      organizationId: selectedOrganizationId || '',
      inquiryId: options?.inquiryId,
      statuses: options?.statuses || [],
      since: options?.since,
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search,
    })
  }, [selectedOrganizationId, options?.inquiryId, options?.statuses, options?.since, options?.limit, options?.offset, options?.search])

  const query = useQuery(listQuotes, request, {
    enabled: !!selectedOrganizationId,
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
  })

  return {
    quotes: query.data?.quotes || [],
    totalCount: query.data?.totalCount || 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Hook to fetch aggregate quote statistics for the current organization.
 *
 * Returns organization-wide totals/counts bucketed to match the quotes list
 * page stat cards. This replaces the previous approach of fetching every quote
 * and computing the buckets client-side.
 *
 * Buckets (see GetQuoteStatsResponse):
 *  - draft    = Draft
 *  - open     = Sent/Viewed (not past valid_until), Revised, unknown
 *  - accepted = Accepted
 *  - inactive = Expired, Rejected, Cancelled, plus Sent/Viewed past valid_until
 *
 * @returns Query result with per-bucket totals (cents) and counts, plus loading/error state
 */
export function useQuoteStats() {
  const { currentOrganization } = useCurrentOrganization()

  const query = useQuery(
    getQuoteStats,
    create(GetQuoteStatsRequestSchema, {
      organizationId: currentOrganization?.id ?? '',
    }),
    { enabled: !!currentOrganization?.id }
  )

  return {
    stats: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    // hasLoaded flips once the query settles (success OR error) so the page's
    // skeleton gate releases even when the stats RPC fails — the stat cards
    // double as tab buttons and must render with placeholder values on error.
    hasLoaded: !query.isPending,
  }
}

/**
 * Hook to fetch quotes for a specific parent entity (inquiry, booking, etc.)
 *
 * This hook follows the same pattern as useNotes and useTasksList:
 * - Fetches quotes filtered by parent type and parent ID
 * - Uses lightweight QuoteLite format optimized for list views
 * - Supports additional filtering by statuses, dates, and pagination
 * - Returns lightweight quote data with pre-calculated totals
 *
 * @param options - Filters for parent type, parent ID, statuses, dates, and pagination
 * @returns Query result with quotes array (QuoteLite[]), total count, loading state, and error
 */
export function useQuotesForParent(options?: {
  parentType?: ParentTypeEnum
  parentId?: string
  statuses?: QuoteStatusEnum[]
  since?: Date
  limit?: number
  offset?: number
}) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  const request = useMemo(() => {
    // Map parentType to the appropriate filter field
    // Currently ListQuotes only supports inquiryId, but this pattern matches notes/tasks
    let inquiryId: string | undefined

    if (options?.parentType && options?.parentId) {
      // For now, we only support PARENT_TYPE_INQUIRY
      // In the future, this could be extended to other parent types
      inquiryId = options.parentId
    }

    return create(ListQuotesRequestSchema, {
      organizationId: selectedOrganizationId || '',
      inquiryId,
      statuses: options?.statuses || [],
      since: options?.since ? dateToTimestamp(options.since) : undefined,
      limit: options?.limit,
      offset: options?.offset,
    })
  }, [selectedOrganizationId, options?.parentType, options?.parentId, options?.statuses, options?.since, options?.limit, options?.offset])

  const query = useQuery(listQuotes, request, {
    enabled: !!selectedOrganizationId && !!options?.parentId,
  })

  return {
    quotes: query.data?.quotes || [],
    totalCount: query.data?.totalCount || 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Hook to fetch quotes by an array of IDs
 *
 * This hook follows the same pattern as useInvoiceLitesByIds:
 * - Fetches lightweight QuoteLite objects for the given IDs
 * - Uses sorted IDs to create stable query keys and prevent unnecessary refetches
 * - Only fetches when there are IDs to fetch
 * - RBAC filtering is handled on the backend
 *
 * Use this when you have a list of quote IDs (e.g., from inquiry.quoteIds) and need
 * to fetch the corresponding QuoteLite data for display.
 *
 * @param ids - Array of quote IDs to fetch
 * @returns Query result with quotes array (QuoteLite[]), loading state, and error
 *
 * @example
 * ```typescript
 * // Get quote IDs from parent entity
 * const { inquiry } = useInquiry(id)
 * const quoteIds = inquiry?.quoteIds || []
 *
 * // Fetch those specific quotes
 * const { quotes, isLoading, hasLoaded } = useQuoteLitesByIds(quoteIds)
 * ```
 */
export function useQuoteLitesByIds(ids: string[]) {
  // Create stable key from sorted IDs to prevent unnecessary refetches
  const sortedIds = useMemo(() => [...ids].sort(), [ids])

  const query = useQuery(
    getQuoteLitesByIds,
    create(GetQuoteLitesByIdsRequestSchema, { ids: sortedIds }),
    {
      enabled: sortedIds.length > 0,
      select: (data) => data?.quotes || [],
    }
  )

  return {
    quotes: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Hook to fetch public quote interactions for a contact
 *
 * Returns public quote token activity attributed to the original recipient contact.
 */
export function useContactQuoteInteractions(options?: {
  contactId?: string
  quoteId?: string
  limit?: number
  offset?: number
}) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  const request = useMemo(() => {
    return create(ListContactQuoteInteractionsRequestSchema, {
      organizationId: selectedOrganizationId || '',
      contactId: options?.contactId || '',
      quoteId: options?.quoteId,
      limit: options?.limit,
      offset: options?.offset,
    })
  }, [selectedOrganizationId, options?.contactId, options?.quoteId, options?.limit, options?.offset])

  const query = useQuery(listContactQuoteInteractions, request, {
    enabled: !!selectedOrganizationId && !!options?.contactId,
  })

  return {
    interactions: query.data?.interactions || [],
    totalCount: query.data?.totalCount || 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

function quoteInteractionTimestampMillis(timestamp?: ContactQuoteInteraction['lastViewedAt']) {
  if (!timestamp) {
    return 0
  }

  const seconds = typeof timestamp.seconds === 'bigint'
    ? Number(timestamp.seconds)
    : timestamp.seconds

  return seconds * 1000 + Math.floor((timestamp.nanos ?? 0) / 1_000_000)
}

function quoteInteractionLatestTimestampMillis(interaction: ContactQuoteInteraction) {
  return Math.max(
    quoteInteractionTimestampMillis(interaction.lastViewedAt),
    quoteInteractionTimestampMillis(interaction.acceptedAt),
    quoteInteractionTimestampMillis(interaction.createdAt)
  )
}

/**
 * Hook to fetch public quote interactions for a contact across specific quotes.
 *
 * Uses the server-side quote IDs filter so lead pages do not truncate
 * contact-level interactions before narrowing to the current lead.
 */
export function useContactQuoteInteractionsByQuoteIds(options?: {
  contactId?: string
  quoteIds?: string[]
  limitPerQuote?: number
  enabled?: boolean
}) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  const sortedQuoteIds = useMemo(() => {
    return [...new Set(options?.quoteIds || [])].sort()
  }, [options?.quoteIds])

  const enabled = !!selectedOrganizationId && (options?.enabled ?? true) && !!options?.contactId && sortedQuoteIds.length > 0

  const request = useMemo(() => {
    return create(ListContactQuoteInteractionsRequestSchema, {
      organizationId: selectedOrganizationId || '',
      contactId: options?.contactId || '',
      quoteIds: sortedQuoteIds,
      limit: options?.limitPerQuote || 100,
      offset: 0,
    })
  }, [selectedOrganizationId, options?.contactId, options?.limitPerQuote, sortedQuoteIds])

  const query = useQuery(listContactQuoteInteractions, request, { enabled })

  const interactions = (query.data?.interactions || [])
    .sort((a, b) => quoteInteractionLatestTimestampMillis(b) - quoteInteractionLatestTimestampMillis(a))

  return {
    interactions,
    totalCount: query.data?.totalCount || 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: !enabled || query.isSuccess || !!query.data,
  }
}

/**
 * Hook to fetch a single quote by ID
 *
 * This hook:
 * - Fetches a fully enriched quote with all nested data
 * - Includes quote items, users, and calculated totals
 * - Returns complete quote data optimized for detail views
 *
 * @param quoteId - The ID of the quote to fetch
 * @returns Query result with quote, loading state, and error
 */
export function useQuote(quoteId?: string) {
  const query = useQuery(
    getQuote,
    create(GetQuoteRequestSchema, {
      id: quoteId ?? '',
    }),
    {
      enabled: !!quoteId,
      select: (data) => data?.quote,
    }
  )

  return {
    quote: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/** Per-id state from `useQuotesByIds`. */
export interface QuoteByIdState {
  quote: Quote | undefined
  isLoading: boolean
  isError: boolean
}

/**
 * Fetches several full quotes in parallel (one `GetQuote` each), sharing cache
 * entries with `useQuote` — so a mutation that updates a quote's `getQuote`
 * cache (e.g. `useUpdateQuote`) refreshes it here too.
 *
 * Use this when a list needs fields `QuoteLite` lacks (events, line items).
 *
 * @param ids - Quote IDs to fetch
 * @returns State keyed by quote ID
 */
export function useQuotesByIds(ids: string[]): Record<string, QuoteByIdState> {
  const transport = useTransport()
  const results = useQueries({
    queries: ids.map((id) => ({
      ...createQueryOptions(getQuote, create(GetQuoteRequestSchema, { id }), { transport }),
      enabled: Boolean(id),
    })),
  })

  const byId: Record<string, QuoteByIdState> = {}
  ids.forEach((id, index) => {
    const result = results[index]
    byId[id] = { quote: result?.data?.quote, isLoading: result?.isLoading ?? false, isError: result?.isError ?? false }
  })
  return byId
}

/**
 * Hook to create a new quote
 * 
 * This hook:
 * - Provides mutation function to create quotes
 * - Automatically invalidates quote list queries on success
 * - Updates cache with the created quote
 * 
 * @returns Mutation result with createQuote function
 */
export function useCreateQuote() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    createQuote,
    {
      onSuccess: (response) => {
        const createdQuote = response.quote

        if (!createdQuote) return

        // Update individual quote cache
        const quoteQueryKey = createConnectQueryKey({
          schema: getQuote,
          transport,
          input: { id: createdQuote.id },
          cardinality: 'finite',
        })

        queryClient.setQueryData(
          quoteQueryKey,
          create(GetQuoteResponseSchema, { quote: createdQuote })
        )

        // Invalidate list queries (for pages that use listQuotes)
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listQuotes,
            transport,
            cardinality: undefined,
          }),
        })

        // Update getQuoteLitesByIds cache directly with the new quote
        // This avoids requiring a refetch when the inquiry's quoteIds change
        if (createdQuote.inquiryId) {
          const inquiryQueryKey = createConnectQueryKey({
            schema: getInquiry,
            transport,
            input: { id: createdQuote.inquiryId },
            cardinality: 'finite',
          })

          // Get current inquiry data from cache
          const cachedInquiryResponse = queryClient.getQueryData<{ inquiry?: { quoteIds?: string[] } }>(inquiryQueryKey)
          const currentQuoteIds = cachedInquiryResponse?.inquiry?.quoteIds || []

          // Get current quotes from the getQuoteLitesByIds cache
          const oldSortedIds = [...currentQuoteIds].sort()
          const oldQuotesQueryKey = createConnectQueryKey({
            schema: getQuoteLitesByIds,
            transport,
            input: create(GetQuoteLitesByIdsRequestSchema, { ids: oldSortedIds }),
            cardinality: 'finite',
          })
          const cachedQuotesResponse = queryClient.getQueryData<{ quotes?: Array<{ id: string }> }>(oldQuotesQueryKey)
          const existingQuotes = cachedQuotesResponse?.quotes || []

          // Convert the new Quote to QuoteLite
          const newQuoteLite = quoteToQuoteLite(createdQuote)

          // Create the new query key with updated IDs
          const newQuoteIds = [...currentQuoteIds, createdQuote.id]
          const newSortedIds = [...newQuoteIds].sort()
          const newQuotesQueryKey = createConnectQueryKey({
            schema: getQuoteLitesByIds,
            transport,
            input: create(GetQuoteLitesByIdsRequestSchema, { ids: newSortedIds }),
            cardinality: 'finite',
          })

          // Pre-populate the cache for the new query key (newest first)
          queryClient.setQueryData(
            newQuotesQueryKey,
            create(GetQuoteLitesByIdsResponseSchema, {
              quotes: [newQuoteLite, ...existingQuotes],
            })
          )

          // Update the inquiry cache with the new quoteIds
          if (cachedInquiryResponse?.inquiry) {
            queryClient.setQueryData(
              inquiryQueryKey,
              create(GetInquiryResponseSchema, {
                inquiry: {
                  ...cachedInquiryResponse.inquiry,
                  quoteIds: newQuoteIds,
                },
              })
            )
          }

          // Invalidate getQuoteLitesByIds caches for background sync
          queryClient.invalidateQueries({
            queryKey: createConnectQueryKey({
              schema: getQuoteLitesByIds,
              transport,
              cardinality: undefined,
            }),
          })

          // Invalidate individual inquiry so quote list updates reliably
          queryClient.invalidateQueries({
            queryKey: createConnectQueryKey({
              schema: getInquiry,
              transport,
              input: { id: createdQuote.inquiryId },
              cardinality: 'finite',
            }),
          })
        }
      },
    }
  )

  return {
    mutation,
    createQuote: async (request: Parameters<typeof mutation.mutateAsync>[0]) => {
      return mutation.mutateAsync(request)
    },
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to update an existing quote
 *
 * This hook provides mutation capabilities for updating quotes with intelligent
 * revision handling based on which fields are modified.
 *
 * **Revision Behavior:**
 * The backend uses the field mask to determine if a new revision should be created:
 *
 * - **Metadata updates** (title, description, status) - Update in-place, no new revision
 * - **Financial changes** (sales_tax_rate, valid_until, discount fields) - Create new revision
 * - **Line item changes** (line_items) - Create new revision
 *
 * Use the `hasLineItemsChanged()` helper to detect line item modifications before updating.
 *
 * **Field Mask:**
 * Always include a field mask in UpdateQuoteRequest to specify which fields to update.
 * This ensures precise control over what changes and whether a revision is created.
 *
 * **Cache Management:**
 * - Automatically updates the individual quote cache with the latest data
 * - Invalidates list queries to reflect changes across the application
 * - Optimistic updates ensure immediate UI feedback
 *
 * @returns Mutation result with updateQuote function, loading state, and error handling
 *
 * @example
 * ```typescript
 * const { updateQuote } = useUpdateQuote()
 *
 * // Update metadata only (no new revision)
 * await updateQuote(create(UpdateQuoteRequestSchema, {
 *   id: quote.id,
 *   title: 'Updated Title',
 *   updateMask: create(FieldMaskSchema, {
 *     paths: ['title']
 *   })
 * }))
 *
 * // Update line items (creates new revision)
 * await updateQuote(create(UpdateQuoteRequestSchema, {
 *   id: quote.id,
 *   lineItems: newLineItems,
 *   updateMask: create(FieldMaskSchema, {
 *     paths: ['line_items']
 *   })
 * }))
 * ```
 */
export function useUpdateQuote() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const listQuotesKey = createConnectQueryKey({
    schema: listQuotes,
    transport,
    cardinality: undefined,
  })

  const mutation = useMutation(
    updateQuote,
    {
      onMutate: async (variables) => {
        // Cancel in-flight list queries so they don't overwrite updates
        await queryClient.cancelQueries({ queryKey: listQuotesKey })

        // Snapshot all list caches for rollback
        const previousLists = queryClient.getQueriesData<ListQuotesResponse>({
          queryKey: listQuotesKey,
        })

        // Cancel and snapshot the individual quote query for optimistic update
        const quoteQueryKey = createConnectQueryKey({
          schema: getQuote,
          transport,
          input: { id: variables.id },
          cardinality: 'finite',
        })
        await queryClient.cancelQueries({ queryKey: quoteQueryKey })
        const previousQuote = queryClient.getQueryData(quoteQueryKey)

        // Optimistically update the individual quote cache
        const cachedResponse = previousQuote as { quote?: Quote } | undefined
        const cachedQuote = cachedResponse?.quote
        if (cachedQuote) {
          const paths = variables.updateMask?.paths || []
          const optimisticQuote = { ...cachedQuote }

          if (paths.includes('title') && variables.title !== undefined) {
            optimisticQuote.title = variables.title
          }
          if (paths.includes('description') && variables.description !== undefined) {
            optimisticQuote.description = variables.description
          }
          if (paths.includes('valid_until') && variables.validUntil) {
            optimisticQuote.validUntil = variables.validUntil as Quote['validUntil']
          }
          if (paths.includes('discount_id')) {
            optimisticQuote.discountId = variables.discountId
          }
          if (paths.includes('discount_name')) {
            optimisticQuote.discountName = variables.discountName
          }
          if (paths.includes('discount_type')) {
            optimisticQuote.discountType = variables.discountType
          }
          if (paths.includes('discount_value')) {
            optimisticQuote.discountValue = variables.discountValue
          }
          if (paths.includes('enforced_tip_enabled')) {
            optimisticQuote.enforcedTipEnabled = variables.enforcedTipEnabled
          }
          if (paths.includes('enforced_tip_percentage')) {
            optimisticQuote.enforcedTipPercentage = variables.enforcedTipPercentage
          }
          if (paths.includes('tip_allocation_mode')) {
            optimisticQuote.tipAllocationMode = variables.tipAllocationMode ?? optimisticQuote.tipAllocationMode
          }
          if (paths.includes('line_items') && variables.lineItems && variables.lineItems.length > 0) {
            optimisticQuote.lineItems = variables.lineItems.map((li, i) =>
              create(QuoteLineItemSchema, {
                id: cachedQuote.lineItems[i]?.id || '',
                quoteRevisionId: cachedQuote.currentRevisionId,
                sourceItemId: li.itemId,
                name: li.name || '',
                description: li.description,
                unitPriceCents: li.unitPriceCents ?? 0n,
                quantity: li.quantity ?? 0,
                unit: li.unit || 'Per Event',
                lineTotalCents: BigInt(calculateLineTotalCents(li.quantity ?? 0, Number(li.unitPriceCents ?? 0n))),
                sortOrder: i,
                isTaxable: li.isTaxable,
                salesTaxRateId: li.salesTaxRateId,
                salesTaxRateName: li.salesTaxRateName,
                salesTaxRate: li.salesTaxRate,
                salesTaxCents: li.salesTaxCents,
              })
            )
          }
          if (paths.includes('checkout_payment_methods')) {
            optimisticQuote.checkoutPaymentMethods = [...(variables.checkoutPaymentMethods ?? [])]
          }
          if (paths.includes('requires_questionnaire') && variables.requiresQuestionnaire !== undefined) {
            optimisticQuote.requiresQuestionnaire = variables.requiresQuestionnaire
          }

          queryClient.setQueryData(
            quoteQueryKey,
            create(GetQuoteResponseSchema, { quote: optimisticQuote })
          )
        }

        return { previousLists, previousQuote, quoteQueryKey }
      },

      onError: (_error, _variables, context) => {
        // Rollback all list caches to their pre-mutation state
        if (context?.previousLists) {
          for (const [key, data] of context.previousLists) {
            queryClient.setQueryData(key, data)
          }
        }
        // Rollback the individual quote cache
        if (context?.quoteQueryKey && context?.previousQuote) {
          queryClient.setQueryData(context.quoteQueryKey, context.previousQuote)
        }
      },

      onSuccess: (response) => {
        const updatedQuote = response.quote
        if (!updatedQuote) return

        // Update individual quote cache
        const queryKey = createConnectQueryKey({
          schema: getQuote,
          transport,
          input: { id: updatedQuote.id },
          cardinality: 'finite',
        })
        queryClient.setQueryData(
          queryKey,
          create(GetQuoteResponseSchema, { quote: updatedQuote })
        )

        // Update all listQuotes caches with the response data
        const existingLists = queryClient.getQueriesData<ListQuotesResponse>({
          queryKey: listQuotesKey,
        })
        for (const [key, data] of existingLists) {
          if (!data?.quotes) continue
          queryClient.setQueryData(key, {
            ...data,
            quotes: data.quotes.map((quote) =>
              quote.id === updatedQuote.id ? quoteToQuoteLite(updatedQuote) : quote
            ),
          })
        }

        // Patch QuoteLite status in getQuoteLitesByIds caches
        queryClient.setQueriesData(
          {
            queryKey: createConnectQueryKey({
              schema: getQuoteLitesByIds,
              transport,
              cardinality: undefined,
            }),
          },
          (old: unknown) => {
            if (!old || typeof old !== 'object') return old
            const data = old as { quotes?: Array<Record<string, unknown>> }
            if (!data.quotes) return old
            return {
              ...data,
              quotes: data.quotes.map(q =>
                q['id'] === updatedQuote.id ? { ...q, status: updatedQuote.status } : q
              ),
            }
          }
        )
      },

      onSettled: () => {
        // Refetch lists in background for eventual consistency
        queryClient.invalidateQueries({ queryKey: listQuotesKey })
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getQuoteLitesByIds,
            transport,
            cardinality: undefined,
          }),
        })
      },
    }
  )

  return {
    mutation,
    updateQuote: async (request: Parameters<typeof mutation.mutateAsync>[0]) => {
      return mutation.mutateAsync(request)
    },
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to update a quote item
 * 
 * This hook:
 * - Provides mutation function to update quote items
 * - Automatically invalidates related quote cache on success
 * 
 * @returns Mutation result with updateQuoteItem function
 */
export function useUpdateQuoteItem() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    updateQuoteItem,
    {
      onSuccess: (response) => {
        const updatedItem = response.quoteItem

        if (!updatedItem) return

        // Invalidate the parent quote's cache to refresh totals
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getQuote,
            transport,
            input: { id: updatedItem.quoteId },
            cardinality: 'finite',
          }),
        })

        // Invalidate quote lists
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listQuotes,
            transport,
            cardinality: undefined,
          }),
        })
      },
    }
  )

  return {
    mutation,
    updateQuoteItem: async (request: Parameters<typeof mutation.mutateAsync>[0]) => {
      return mutation.mutateAsync(request)
    },
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to prefetch a quote on hover
 *
 * This hook provides a prefetch function that can be called on hover
 * to load quote data into the cache before the user clicks.
 * Skips prefetching if the data is already in the cache.
 *
 * @returns Prefetch function
 */
export function usePrefetchQuote() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const prefetchQuote = async (quoteId: string) => {
    const queryKey = createConnectQueryKey({
      schema: getQuote,
      transport,
      input: { id: quoteId },
      cardinality: 'finite',
    })

    // Skip prefetch if data is already in cache
    const cachedData = queryClient.getQueryData(queryKey)
    if (cachedData) {
      return
    }

    await queryClient.prefetchQuery(
      getQuoteQueryOptions(quoteId, transport)
    )
  }

  return { prefetchQuote }
}

/**
 * Hook to send a quote via email
 *
 * This hook:
 * - Sends a quote email to the customer
 * - Updates quote status from DRAFT to SENT
 * - Automatically updates the individual quote cache
 * - Invalidates list queries to reflect status change
 *
 * The backend validates that the quote is in DRAFT status before sending.
 * On success, returns the updated quote with SENT status.
 *
 * @returns Mutation result with sendQuote function, loading state, and error handling
 *
 * @example
 * ```typescript
 * const { sendQuote, isLoading } = useSendQuote()
 *
 * await sendQuote(create(SendQuoteRequestSchema, {
 *   id: quote.id
 * }))
 * ```
 */
export function updateSendQuoteCaches(
  queryClient: QueryClient,
  transport: Transport,
  updatedQuote: Quote,
) {
  // Update individual quote cache
  const queryKey = createConnectQueryKey({
    schema: getQuote,
    transport,
    input: { id: updatedQuote.id },
    cardinality: 'finite',
  })

  queryClient.setQueryData(
    queryKey,
    create(GetQuoteResponseSchema, { quote: updatedQuote })
  )

  // Invalidate list queries to reflect status change
  queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: listQuotes,
      transport,
      cardinality: undefined,
    }),
  })

  // Invalidate inquiry/lead lists since they display quote status
  queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: listInquiries,
      transport,
      cardinality: undefined,
    }),
  })

  // Invalidate the dashboard aggregate so its lead cards refresh too
  queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: getDashboardSummary,
      transport,
      cardinality: undefined,
    }),
  })

  // Immediately patch QuoteLite status in cache so cards update without waiting for refetch
  queryClient.setQueriesData(
    {
      queryKey: createConnectQueryKey({
        schema: getQuoteLitesByIds,
        transport,
        cardinality: undefined,
      }),
    },
    (old: unknown) => {
      if (!old || typeof old !== 'object') return old
      const data = old as { quotes?: Array<Record<string, unknown>> }
      if (!data.quotes) return old
      return {
        ...data,
        quotes: data.quotes.map(q =>
          q['id'] === updatedQuote.id ? { ...q, status: updatedQuote.status } : q
        ),
      }
    }
  )

  // Invalidate getQuoteLitesByIds caches for background sync
  queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: getQuoteLitesByIds,
      transport,
      cardinality: undefined,
    }),
  })

  // Invalidate individual inquiry so stage badge updates in lead detail view
  if (updatedQuote.inquiryId) {
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: getInquiry,
        transport,
        input: { id: updatedQuote.inquiryId },
        cardinality: 'finite',
      }),
    })
  }

  // Refresh the record's Conversations (and legacy Emails) tab — the sent quote
  // email is now persisted
  invalidateCrossEntityEmailCaches(queryClient, transport)
}

interface UseSendQuoteOptions {
  onSuccess?: () => void
}

export function useSendQuote(options: UseSendQuoteOptions = {}) {
  const queryClient = useQueryClient()
  const transport = useTransport()
  const pendingOperationKeys = useRef(new Map<string, string>())

  const mutation = useMutation(
    sendQuote,
    {
      onSuccess: (response) => {
        const updatedQuote = response.quote

        if (!updatedQuote) return
        updateSendQuoteCaches(queryClient, transport, updatedQuote)
        options.onSuccess?.()
      },
    }
  )

  return {
    mutation,
    sendQuote: async (request: Parameters<typeof mutation.mutateAsync>[0]) => {
      const quoteId = request.id
      if (!quoteId) throw new Error('Quote ID is required')

      const operationKey = request.idempotencyKey
        ?? pendingOperationKeys.current.get(quoteId)
        ?? crypto.randomUUID()
      pendingOperationKeys.current.set(quoteId, operationKey)

      const response = await mutation.mutateAsync(create(SendQuoteRequestSchema, {
        id: quoteId,
        idempotencyKey: operationKey,
        additionalRecipientEmails: request.additionalRecipientEmails ?? [],
        emailOverride: request.emailOverride,
      }))
      // A later user-initiated resend is a new logical operation. Retain the key
      // only across failed or ambiguous attempts of the current operation.
      pendingOperationKeys.current.delete(quoteId)
      return response
    },
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to duplicate an existing quote
 *
 * This hook:
 * - Creates a copy of an existing quote with all its data
 * - Appends " Copy" to the quote title
 * - Resets status to DRAFT
 * - Copies line items, payment schedules, event details, discount, enforced tip
 * - Automatically updates the individual quote cache with the new quote
 * - Invalidates list queries to include the new quote
 *
 * @returns Mutation result with duplicateQuote function, loading state, and error handling
 *
 * @example
 * ```typescript
 * const { duplicateQuote, isLoading } = useDuplicateQuote()
 *
 * const response = await duplicateQuote(create(DuplicateQuoteRequestSchema, {
 *   id: quote.id
 * }))
 * // response.quote contains the newly created duplicate quote
 * ```
 */
export function useDuplicateQuote() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    duplicateQuote,
    {
      onSuccess: (response) => {
        const newQuote = response.quote

        if (!newQuote) return

        // Update individual quote cache for the new quote
        const quoteQueryKey = createConnectQueryKey({
          schema: getQuote,
          transport,
          input: { id: newQuote.id },
          cardinality: 'finite',
        })

        queryClient.setQueryData(
          quoteQueryKey,
          create(GetQuoteResponseSchema, { quote: newQuote })
        )

        // Invalidate list queries (for pages that use listQuotes)
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listQuotes,
            transport,
            cardinality: undefined,
          }),
        })

        // Update getQuoteLitesByIds cache directly with the new quote
        // This avoids requiring a refetch when the inquiry's quoteIds change
        if (newQuote.inquiryId) {
          const inquiryQueryKey = createConnectQueryKey({
            schema: getInquiry,
            transport,
            input: { id: newQuote.inquiryId },
            cardinality: 'finite',
          })

          // Get current inquiry data from cache
          const cachedInquiryResponse = queryClient.getQueryData<{ inquiry?: { quoteIds?: string[] } }>(inquiryQueryKey)
          const currentQuoteIds = cachedInquiryResponse?.inquiry?.quoteIds || []

          // Get current quotes from the getQuoteLitesByIds cache
          const oldSortedIds = [...currentQuoteIds].sort()
          const oldQuotesQueryKey = createConnectQueryKey({
            schema: getQuoteLitesByIds,
            transport,
            input: create(GetQuoteLitesByIdsRequestSchema, { ids: oldSortedIds }),
            cardinality: 'finite',
          })
          const cachedQuotesResponse = queryClient.getQueryData<{ quotes?: Array<{ id: string }> }>(oldQuotesQueryKey)
          const existingQuotes = cachedQuotesResponse?.quotes || []

          // Convert the new Quote to QuoteLite
          const newQuoteLite = quoteToQuoteLite(newQuote)

          // Create the new query key with updated IDs
          const newQuoteIds = [...currentQuoteIds, newQuote.id]
          const newSortedIds = [...newQuoteIds].sort()
          const newQuotesQueryKey = createConnectQueryKey({
            schema: getQuoteLitesByIds,
            transport,
            input: create(GetQuoteLitesByIdsRequestSchema, { ids: newSortedIds }),
            cardinality: 'finite',
          })

          // Pre-populate the cache for the new query key (newest first)
          queryClient.setQueryData(
            newQuotesQueryKey,
            create(GetQuoteLitesByIdsResponseSchema, {
              quotes: [newQuoteLite, ...existingQuotes],
            })
          )

          // Update the inquiry cache with the new quoteIds
          if (cachedInquiryResponse?.inquiry) {
            queryClient.setQueryData(
              inquiryQueryKey,
              create(GetInquiryResponseSchema, {
                inquiry: {
                  ...cachedInquiryResponse.inquiry,
                  quoteIds: newQuoteIds,
                },
              })
            )
          }

          // Invalidate getQuoteLitesByIds caches for background sync
          queryClient.invalidateQueries({
            queryKey: createConnectQueryKey({
              schema: getQuoteLitesByIds,
              transport,
              cardinality: undefined,
            }),
          })

          // Invalidate individual inquiry so quote list updates reliably
          queryClient.invalidateQueries({
            queryKey: createConnectQueryKey({
              schema: getInquiry,
              transport,
              input: { id: newQuote.inquiryId },
              cardinality: 'finite',
            }),
          })
        }
      },
    }
  )

  return {
    mutation,
    duplicateQuote: async (request: Parameters<typeof mutation.mutateAsync>[0]) => {
      return mutation.mutateAsync(request)
    },
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to generate (or retrieve the cached) PDF for a quote.
 *
 * @returns Mutation result with generatePdf function, loading state, and error handling
 *
 * @example
 * ```typescript
 * const { generatePdf, isLoading } = useGenerateQuotePdf()
 *
 * const response = await generatePdf(
 *   create(GenerateQuotePdfRequestSchema, { quoteId: quote.id })
 * )
 * // response.downloadUrl / response.filename feed downloadFile()
 * ```
 */
/**
 * Deletes a quote. On success the quote's own cache entry is dropped and every
 * list that can show it (quote lists, quote lites, the owning inquiry) refetches.
 */
export function useDeleteQuote() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(deleteQuote, {
    onSuccess: (_response, variables) => {
      queryClient.removeQueries({
        queryKey: createConnectQueryKey({ schema: getQuote, transport, input: { id: variables.id }, cardinality: 'finite' }),
      })
      queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: listQuotes, transport, cardinality: undefined }) })
      queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: getQuoteLitesByIds, transport, cardinality: undefined }) })
      queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: getInquiry, transport, cardinality: undefined }) })
    },
  })

  return {
    deleteQuote: (quoteId: string) => mutation.mutateAsync(create(DeleteQuoteRequestSchema, { id: quoteId })),
    isLoading: mutation.isPending,
  }
}

export function useGenerateQuotePdf() {
  const mutation = useMutation(generateQuotePdf)

  return {
    generatePdf: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}
